import Icon from '@mdi/react';
import { Box, IconButton, Stack, SxProps } from '@mui/material';
import React, { useCallback, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { defaultBorder, vars } from '../../../Theme';
import { getFileTypeOrDefault, IFileInfo } from '../../../util/FileUtils';
import FileIcon from '../Icons/FileIcon';
import MainField from '../TextBlocks/MainField';
import { IAppReduxStore } from '../../../redux/store';
import { useSelector } from 'react-redux';
import { getDocTableActionsConfig } from '../../../redux/project/sliceProjectActions';
import AppTooltip from '../AppTooltip/AppTooltip';
import { useTranslation } from 'react-i18next';
import { ActionName } from '../DocTable/DocTableActions';
import { IDocDefaultFields } from '../../../redux/project/documents/sliceProjectDocuments';

/** Sidepanel actions */
const sidepanelActions: ['view', 'download'] = ['view', 'download'];

/** Styles for the main component element. */
const stackSx: SxProps = {
    borderBottom: defaultBorder,

    '&:hover': {
        bgcolor: vars.bgGray,
    },
};

/**
 * Shows information about a file attachment for a document.
 */
export default ({
    fileData,
    type,
    index,
    sidepanelData,
}: {
    fileData: IFileInfo;
    type: string;
    index: number;
    sidepanelData: IDocDefaultFields;
}) => {
    /** Configure component translation. */
    const { i18n } = useTranslation();

    /** State slice that controls the visibility of the component's hover buttons. */
    const [showHoverButtons, setShowHoverButtons] = useState(false);

    /** Get the replicaId from the react router. */
    const { replicaId } = useParams();

    const fileTypes = useSelector((store: IAppReduxStore) => store.projectFileTypes.data);
    const fallback = useSelector((store: IAppReduxStore) => store.projectFileTypes.default);
    const actionsConfigData = useSelector(getDocTableActionsConfig);

    /**
     * Callback that makes the hover buttons visible when the user's mouse hovers over the component element.
     */
    const handleMouseEnter = useCallback(() => setShowHoverButtons(true), []);

    /**
     * Callback that hides the hover buttons when the user's mouse moves away from the component element.
     */
    const handleMouseLeave = useCallback(() => setShowHoverButtons(false), []);

    /* Gets file type and its properties. **/
    const documentFileType = useMemo(
        () => getFileTypeOrDefault(fileData?.fileName),
        [fileData?.fileName, fileTypes, fallback],
    );

    /**
     * Creates a button for the component.
     * @param btnId - Button id.
     */
    const createBtn = (btnId: ActionName) => {
        if (
            !actionsConfigData?.[btnId] ||
            (btnId === 'view' && !documentFileType?.extensions?.length)
        ) {
            return null;
        }

        /** Command definition. */
        const { icon, translations, onClick } = actionsConfigData[btnId];

        /**
         * Handles the Button's onclick event.
         */
        const handleOnClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();

            onClick({
                replicaId,
                idDocIndex: sidepanelData.IdDocIndex,
                fileName: fileData.fileName,
                fieldName: type,
                fieldIndex: `${index}`,
                universalId: sidepanelData.UniversalID,
            });
        };

        return actionsConfigData?.[btnId] ? (
            <AppTooltip key={btnId} title={translations[i18n.language]} placement='top-start'>
                <IconButton
                    onClick={handleOnClick}
                    sx={{
                        borderRadius: 1,
                    }}
                >
                    <Icon path={icon} size={1} color={vars.font} />
                </IconButton>
            </AppTooltip>
        ) : null;
    };

    /**
     * Renders the hover buttons if the user's mouse is over the component element.
     */
    const getHoverButtons = useCallback(
        () =>
            showHoverButtons ? (
                <Stack direction='row'>{sidepanelActions.map(createBtn).filter(Boolean)}</Stack>
            ) : (
                <></>
            ),
        [showHoverButtons],
    );

    return fileData ? (
        <Stack
            direction='row'
            sx={stackSx}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <FileIcon fileName={fileData.fileName} />

            <Stack sx={{ p: 0.5, overflow: 'hidden', flexGrow: 4 }}>
                <MainField text={fileData.fileName} sx={{ flexGrow: 4 }} />

                <Box sx={{ py: 0.25, mr: 2, fontSize: 12 }}>{fileData.size}</Box>
            </Stack>
            {getHoverButtons()}
        </Stack>
    ) : (
        <></>
    );
};
